import type { SxProps, Theme } from "@mui/material";
import { Typography } from "@mui/material";
import type { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-premium";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { CashFlowValue, IrrDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";
import { createDrillDownPageAction } from "../../../../../store/utils/storeUtils";

import { createCashFlowDrillDownPage } from "./cashFlowDrillDown.ts";

interface Props {
  configuration: DrillDownPage;
  info: IrrDrillDownInfo;
}
export function CashFlowGrid({ configuration, info }: Props) {
  const dispatch = useDispatch();
  const records = useMemo((): CashFlowValueRecord[] => {
    return (info.cashFlowValues || []).map((v) => ({ ...v, id: generateGuid() }));
  }, [info.cashFlowValues]);

  const handleCellClick = useCallback(
    (row: CashFlowValueRecord) => {
      if (row.drillDownInfo === undefined) return;

      const newConfiguration = createCashFlowDrillDownPage(
        configuration.reportId,
        getActiveLayer(configuration).measure,
        row
      );
      if (newConfiguration !== undefined) {
        dispatch(createDrillDownPageAction("add", newConfiguration));
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [configuration]
  );

  const columns = useMemo((): GridColDef<CashFlowValueRecord>[] => {
    return [
      {
        field: nameof<CashFlowValueRecord>("description"),
        width: 150,
        renderHeader: () => <></>,
        renderCell: (params) => renderNav(params.row),
        ...getDefaultHeaderSettings(),
      },
      {
        field: nameof<CashFlowValueRecord>("dateFormatted"),
        flex: 1,
        headerName: info.referenceDateFieldCaption || "Date",
        ...getDefaultHeaderSettings(),
      },
      {
        field: nameof<CashFlowValueRecord>("amountFormatted"),
        headerName: info.amountFieldCaption || "Amount",
        width: 190,
        maxWidth: 210,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => <AmountCellRenderer row={params.row} onClick={handleCellClick}></AmountCellRenderer>,
        ...getDefaultHeaderSettings(),
      },
      {
        field: "id",
        renderHeader: () => <></>,
        renderCell: () => <></>,
        flex: 1.5,
        ...getDefaultHeaderSettings(),
      },
    ];
  }, [handleCellClick, info.amountFieldCaption, info.referenceDateFieldCaption]);

  const topPinnedRows = useMemo(() => {
    return records.filter((r) => r.isNav === true && r.isBeginningOfPeriod === true);
  }, [records]);
  const bottomPinnedRows = useMemo(() => records.filter((r) => r.isNav === true && !r.isBeginningOfPeriod), [records]);

  if (records.length === 0) {
    return <>No data</>;
  }
  return (
    <DataGridPremium<CashFlowValueRecord>
      getRowId={getRowId}
      getRowClassName={getRowClassName}
      rows={records}
      columns={columns}
      rowHeight={36}
      columnHeaderHeight={36}
      hideFooter
      pinnedRows={{ top: topPinnedRows, bottom: bottomPinnedRows }}
      rowSelection={false}
      sx={(theme) => ({
        ".MuiDataGrid-main": {
          flexGrow: 0,
        },
        ".MuiDataGrid-pinnedRows": {
          bgcolor: theme.palette.background.paper,
          "&.MuiDataGrid-pinnedRows--top": {
            boxShadow: "0px 1px 4px -2px rgba(0, 0, 0, 0.21)",
          },
          "&.MuiDataGrid-pinnedRows--bottom": {
            boxShadow: "0px -2px 4px -2px rgba(0, 0, 0, 0.21)",
          },
        },
        ".MuiDataGrid-container--top [role=row], .MuiDataGrid-container--bottom [role=row]": {
          "&.nav-row": {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        },
      })}
    />
  );
}

type CashFlowValueRecord = CashFlowValue & { id: string };

function getRowId(row: { id: string }) {
  return row.id;
}

function getRowClassName(params: GridRowClassNameParams<CashFlowValueRecord>): string {
  return params.row.isNav === true ? "nav-row" : "";
}

function renderNav(row: CashFlowValueRecord) {
  if (row.isNav === true) {
    return <Typography variant="subtitle2">{row.description}</Typography>;
  }
  return null;
}

function getDefaultHeaderSettings(): Partial<GridColDef> {
  return {
    aggregable: false,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
  };
}

function AmountCellRenderer({
  row,
  onClick,
}: {
  row: CashFlowValueRecord;
  onClick: (row: CashFlowValueRecord) => void;
}) {
  const handleCellClick = useCallback(() => {
    onClick(row);
  }, [onClick, row]);

  const sx = useMemo((): SxProps<Theme> => {
    if (row.drillDownInfo === undefined) return {};
    return {
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    };
  }, [row.drillDownInfo]);

  return (
    <Typography variant={row.isNav ? "subtitle2" : "body1"} align="right" sx={{ ...sx }} onClick={handleCellClick}>
      {row.amountFormatted}
    </Typography>
  );
}
